/**
 * Campaign Store (Zustand)
 *
 * @deprecated This store mixes data cache with UI state.
 * Migrate to the new architecture:
 *
 * **Data fetching/mutations** → Use TanStack Query hooks:
 * ```tsx
 * import { useCampaigns, useCreateCampaign } from '@features/campaigns/queries';
 * ```
 *
 * **UI-only state** → Use the new UI store:
 * ```tsx
 * import { useCampaignUIStore } from '@features/campaigns/store/campaignUIStore';
 * ```
 *
 * This store will be removed in a future version.
 * See: apps/web/docs/ARCHITECTURE_MIGRATION_PLAN.md
 *
 * ---
 * Legacy: Global state management for campaign data with CRUD operations.
 * All data is read from FastAPI /api/campaigns endpoints.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { flushSync } from 'react-dom';
import { toast } from '@shared/utils/notifications';
import * as campaignService from '../api/campaignApi';
import { getLastSelectedCampaign, setLastSelectedCampaign } from '@shared/services/userSettingsApi';
import { getErrorMessage } from '@shared/utils/errorHandler';
import { logger } from '@shared/utils/logger';
import { useOrganizationStore } from '@features/organizations/store/organizationStore';
import type {
  CampaignResponse,
  CampaignCreateRequest,
  CampaignUpdateRequest,
  CampaignListParams,
  CampaignLabel,
  CampaignFilters,
  CampaignViewMode,
  CampaignPriority,
  CampaignType,
  BulkUpdateRequest,
} from '@shared/types/api';

/**
 * Campaign store state and actions
 */
interface CampaignStore {
  // State
  campaigns: CampaignResponse[];
  selectedIds: Set<string>;
  selectedCampaignId: string | null; // Global active campaign for filtering other views
  lastCampaignLoadedAt: string | null; // Timestamp when campaign was last loaded
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  statusFilter: 'all' | 'active' | 'archived';

  // Enhanced state
  availableLabels: CampaignLabel[]; // Global label catalog
  availableTags: string[]; // Global tag catalog (extracted from campaigns)
  filters: CampaignFilters; // Active filter state
  viewMode: CampaignViewMode; // List, kanban, or calendar
  starredCampaigns: Set<string>; // Starred campaign IDs

  // Sorting state
  sortField: 'status' | 'priority' | 'campaign_name' | 'owner' | 'last_modified' | 'org_count' | 'progress_percentage' | null;
  sortDirection: 'asc' | 'desc';

  // Category navigation state
  expandedCategories: Set<string>; // e.g., Set(['priority', 'status'])
  activeCategory: string | null; // e.g., 'priority:high', 'status:active', 'standalone:active'

  // Actions
  fetchCampaigns: (params?: CampaignListParams) => Promise<void>;
  createCampaign: (data: CampaignCreateRequest) => Promise<CampaignResponse | null>;
  duplicateCampaign: (id: string) => Promise<void>;
  updateCampaign: (id: string, data: CampaignUpdateRequest) => Promise<void>;
  updateCampaignWithRetry: (id: string, data: CampaignUpdateRequest, maxRetries?: number) => Promise<void>;
  deleteCampaign: (id: string) => Promise<void>;
  archiveCampaign: (id: string) => Promise<void>;
  unarchiveCampaign: (id: string) => Promise<void>;
  activateCampaign: (id: string) => Promise<void>;
  deactivateCampaign: (id: string) => Promise<void>;
  renameCampaign: (id: string, newName: string) => Promise<void>;
  updateCampaignPriority: (id: string, priority: CampaignPriority) => Promise<void>;
  updateCampaignStatus: (id: string, status: CampaignResponse['status']) => Promise<void>;
  saveToCampaign: (campaignId: string, organizationIds: number[]) => Promise<void>;
  setSelectedCampaign: (id: string | null) => void;

  // Enhanced actions
  toggleCampaignStar: (id: string) => Promise<void>;
  addLabelsToCampaign: (id: string, labels: CampaignLabel[]) => Promise<void>;
  removeLabelsFromCampaign: (id: string, labelIds: string[]) => Promise<void>;
  addTagsToCampaign: (id: string, tags: string[]) => Promise<void>;
  removeTagsFromCampaign: (id: string, tags: string[]) => Promise<void>;
  bulkUpdateCampaigns: (request: BulkUpdateRequest) => Promise<void>;
  bulkStarCampaigns: (campaignIds: string[], starred: boolean) => Promise<void>;

  // Label management
  fetchAvailableLabels: () => void; // Extract labels from campaigns
  createLabel: (label: CampaignLabel) => void; // Add to available labels
  updateLabel: (label: CampaignLabel) => void; // Update in available labels
  deleteLabel: (labelId: string) => void; // Remove from available labels

  // Tag management
  fetchAvailableTags: () => void; // Extract tags from campaigns

  // Selection
  toggleSelection: (id: string) => void;
  clearSelection: () => void;
  selectAll: () => void;

  // Filters
  setSearchQuery: (query: string) => void;
  setStatusFilter: (filter: 'all' | 'active' | 'archived') => void;
  setFilters: (filters: Partial<CampaignFilters>) => void;
  clearFilters: () => void;
  getFilteredCampaigns: () => CampaignResponse[];

  // Sorting
  setSorting: (field: CampaignStore['sortField'], direction: 'asc' | 'desc') => void;

  // Category navigation
  toggleCategory: (category: string) => void;
  setActiveCategory: (category: string | null) => void;

  // View mode
  setViewMode: (mode: CampaignViewMode) => void;

  // Utility
  clearError: () => void;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState = {
  campaigns: [],
  selectedIds: new Set<string>(),
  selectedCampaignId: null,
  lastCampaignLoadedAt: null,
  isLoading: false,
  error: null,
  searchQuery: '',
  statusFilter: 'all' as const,

  // Enhanced state
  availableLabels: [],
  availableTags: [],
  filters: {
    status: 'all' as const,
    labels: [],
    tags: [],
    starred: undefined,
    priority: [],
    campaign_type: [],
    owner: undefined,
    due_date_from: undefined,
    due_date_to: undefined,
    search: '',
  },
  viewMode: 'list' as CampaignViewMode,
  starredCampaigns: new Set<string>(),

  // Sorting state (default: status descending)
  sortField: 'status' as const,
  sortDirection: 'desc' as const,

  // Category navigation state
  expandedCategories: new Set<string>(['folders', 'labels', 'tags']), // All navigation sections expanded by default
  activeCategory: null,
};

const CAMPAIGN_PRIORITIES: readonly CampaignPriority[] = ['low', 'medium', 'high', 'urgent'];
const CAMPAIGN_TYPES: readonly CampaignType[] = ['drop', 'event', 'email', 'multi-channel'];

const toCampaignPriority = (value: string | undefined): CampaignPriority | undefined =>
  value && CAMPAIGN_PRIORITIES.includes(value as CampaignPriority)
    ? (value as CampaignPriority)
    : undefined;

const toCampaignType = (value: string | undefined): CampaignType | undefined =>
  value && CAMPAIGN_TYPES.includes(value as CampaignType)
    ? (value as CampaignType)
    : undefined;

/**
 * Migrate legacy localStorage data from 'campaign-store' to 'campaign-storage'
 */
const migrateLegacyStorage = () => {
  const LEGACY_KEY = 'campaign-store';
  const NEW_KEY = 'campaign-storage';
  const MIGRATION_FLAG = 'campaign-store-migrated';

  // Only migrate once
  if (localStorage.getItem(MIGRATION_FLAG)) {
    return;
  }

  try {
    const legacyData = localStorage.getItem(LEGACY_KEY);
    if (legacyData) {
      const parsed = JSON.parse(legacyData);

      // Migrate only selectedCampaignId (legacy store only persisted this field)
      if (parsed?.state?.selectedCampaignId) {
        const newData = {
          state: {
            selectedCampaignId: parsed.state.selectedCampaignId,
            // Initialize other persisted fields with defaults
            campaigns: [],
            lastCampaignLoadedAt: null,
            availableLabels: [],
            availableTags: [],
            filters: initialState.filters,
            viewMode: 'list' as CampaignViewMode,
            starredCampaigns: [],
            sortField: 'status',
            sortDirection: 'desc',
            expandedCategories: ['folders', 'labels', 'tags'],
          },
          version: 0,
        };

        localStorage.setItem(NEW_KEY, JSON.stringify(newData));
      }

      // Clear legacy key after migration
      localStorage.removeItem(LEGACY_KEY);
    }

    // Set migration flag
    localStorage.setItem(MIGRATION_FLAG, 'true');
  } catch (error) {
    logger.error('[CampaignStore] Failed to migrate legacy storage:', error);
  }
};

// Run migration before creating store
migrateLegacyStorage();

/**
 * Utility: Clear stale campaign data from localStorage
 *
 * This can be called from the browser console to manually clear stale campaign data:
 * ```
 * window.clearStaleCampaignData()
 * ```
 */
if (typeof window !== 'undefined') {
  (window as unknown as Window & Record<string, unknown>).clearStaleCampaignData = () => {
    try {
      const STORAGE_KEY = 'campaign-storage';
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);

        // Clear selectedCampaignId
        if (parsed?.state?.selectedCampaignId) {
          logger.warn('[Campaign Debug] Clearing stale campaign ID:', parsed.state.selectedCampaignId);
          parsed.state.selectedCampaignId = null;
          parsed.state.lastCampaignLoadedAt = null;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } catch (error) {
      logger.error('[Campaign Debug] Failed to clear stale campaign data:', error);
      return false;
    }
  };

  logger.log('[CampaignStore] Utility function registered: window.clearStaleCampaignData()');
}

/**
 * Create campaign store with persistence
 */
export const useCampaignStore = create<CampaignStore>()(
  persist(
    (set, get) => ({
  ...initialState,

  /**
   * Fetch campaigns from API
   *
   * @deprecated Use `useCampaigns()` from `@features/campaigns/queries` instead.
   * @param params - Optional filter parameters (status)
   * @example
   * ```tsx
   * // New way (TanStack Query):
   * import { useCampaigns } from '@features/campaigns/queries';
   * const { data: campaigns, isLoading } = useCampaigns({ status: 'active' });
   *
   * // Legacy way (deprecated):
   * const { fetchCampaigns } = useCampaignStore();
   * await fetchCampaigns({ status: 'active' });
   * ```
   */
  fetchCampaigns: async (params?: CampaignListParams) => {
    set({ isLoading: true, error: null });

    try {
      const result = await campaignService.fetchCampaigns(params);

      // Extract starred campaign IDs
      const starredIds = new Set<string>();
      result.campaigns.forEach((campaign) => {
        if (campaign.starred) {
          starredIds.add(campaign.campaign_id);
        }
      });

      // Auto-load last campaign or select first active campaign
      // Priority: 1) PostgreSQL cloud storage, 2) localStorage (fallback), 3) first active campaign
      let persistedCampaignId = get().selectedCampaignId;
      const previousCampaigns = get().campaigns; // Get previous state for status change detection

      // Try to load from PostgreSQL cloud storage first (overrides localStorage)
      try {
        const cloudCampaignId = await getLastSelectedCampaign();
        if (cloudCampaignId) {
          persistedCampaignId = cloudCampaignId;
        }
      } catch (cloudError) {
        logger.warn('[CampaignStore] Could not load campaign from cloud, using localStorage fallback:', cloudError);
      }

      let newSelectedCampaignId = persistedCampaignId;

      // Debug: Log what we're working with
      logger.log('[CampaignStore] Validating campaign selection:', {
        persistedCampaignId,
        availableCampaigns: result.campaigns.map(c => c.campaign_id),
      });

      if (persistedCampaignId) {
        // Check if persisted campaign still exists in fetched campaigns
        const persistedCampaign = result.campaigns.find(
          c => c.campaign_id === persistedCampaignId
        );

        if (persistedCampaign) {
          // ✅ NEW: Detect status change
          const prevCampaign = previousCampaigns.find(c => c.campaign_id === persistedCampaignId);

          if (prevCampaign && prevCampaign.status !== persistedCampaign.status) {
            logger.warn(
              `[CampaignStore] Status changed: ${prevCampaign.status} → ${persistedCampaign.status} for campaign "${persistedCampaign.campaign_name}"`
            );

            // If was ACTIVE but now not, show notification and auto-select first active campaign
            if (prevCampaign.status === 'active' && persistedCampaign.status !== 'active') {
              const firstActive = result.campaigns.find(c => c.status === 'active');
              if (firstActive) {
                newSelectedCampaignId = firstActive.campaign_id;
                toast.info(`Campaign "${prevCampaign.campaign_name}" is no longer active. Switched to "${firstActive.campaign_name}".`);
              } else {
                // Keep current selection even though not active
                newSelectedCampaignId = persistedCampaignId;
                toast.warning(`Campaign "${prevCampaign.campaign_name}" is no longer active. No active campaigns available.`);
                logger.warn('[CampaignStore]   No active campaigns available for auto-selection');
              }
            } else {
              // Status changed but not from ACTIVE, keep current selection
              newSelectedCampaignId = persistedCampaign.campaign_id;
            }
          } else {
            // No status change, keep current selection
            newSelectedCampaignId = persistedCampaign.campaign_id;
            logger.log(`[CampaignStore] ✓ Auto-loaded last campaign: ${persistedCampaign.campaign_name} (${persistedCampaign.campaign_id})`);
          }
        } else {
          // Fallback: Campaign deleted or no longer in DB, clear persisted ID and use first active
          logger.warn('[CampaignStore] ✗ Last campaign no longer exists in database:', persistedCampaignId);
          logger.warn('[CampaignStore]   Clearing stale campaign ID from localStorage and selecting first active campaign');
          newSelectedCampaignId = null; // Clear invalid persisted ID
          const firstActive = result.campaigns.find(c => c.status === 'active');
          if (firstActive) {
            newSelectedCampaignId = firstActive.campaign_id;
            logger.log(`[CampaignStore] ✓ Auto-selected first active campaign: ${firstActive.campaign_name} (${firstActive.campaign_id})`);
          } else {
            logger.warn('[CampaignStore]   No active campaigns available');
          }
        }
      } else {
        // No persisted campaign, select first active
        const firstActive = result.campaigns.find(c => c.status === 'active');
        if (firstActive) {
          newSelectedCampaignId = firstActive.campaign_id;
          logger.log(`[CampaignStore] ✓ Auto-selected first active campaign: ${firstActive.campaign_name} (${firstActive.campaign_id})`);
        } else {
          logger.warn('[CampaignStore]   No active campaigns available for auto-selection');
        }
      }

      // DEBUG: Log label_contact_selections for the selected campaign
      const selectedCampaign = result.campaigns.find(c => c.campaign_id === newSelectedCampaignId);
      if (selectedCampaign) {
        console.log('[DEBUG Include] fetchCampaigns completed - selected campaign:', selectedCampaign.campaign_name);
        console.log('[DEBUG Include] label_contact_selections:', selectedCampaign.label_contact_selections);
        console.log('[DEBUG Include] Number of orgs with selections:', Object.keys(selectedCampaign.label_contact_selections || {}).length);
      }

      set({
        campaigns: [...result.campaigns],  // ✅ Force new array reference for Zustand immutability
        starredCampaigns: starredIds,
        selectedCampaignId: newSelectedCampaignId,
        isLoading: false,
      });

      // Auto-extract available labels and tags from campaigns
      get().fetchAvailableLabels();
      get().fetchAvailableTags();
    } catch (error) {
      logger.error('[CampaignStore] Failed to fetch campaigns:', error);
      set({
        error: (error as Error).message || 'Failed to fetch campaigns',
        isLoading: false,
      });
    }
  },

  /**
   * Create new campaign
   *
   * @deprecated Use `useCreateCampaign()` from `@features/campaigns/queries` instead.
   * @param data - Campaign creation data
   * @example
   * ```tsx
   * // New way (TanStack Query):
   * import { useCreateCampaign } from '@features/campaigns/queries';
   * const { mutate } = useCreateCampaign();
   * mutate({ name: 'Q4 2025 Campaign', priority: 'high' });
   * ```
   */
  createCampaign: async (data: CampaignCreateRequest) => {
    set({ isLoading: true, error: null });

    try {
      const newCampaign = await campaignService.createCampaign(data);

      set((state) => ({
        campaigns: [...state.campaigns, newCampaign],
        isLoading: false,
      }));

      // Show success notification
      toast.success(`Campaign "${newCampaign.campaign_name}" created!`);

      // Auto-add to "My Campaigns" system folder
      // Wait a bit to ensure campaign is fully persisted before folder assignment
      try {
        const { useFolderStore } = await import('../../navigation/store/folderStore');
        const { folders, addCampaignToFolder } = useFolderStore.getState();
        const myCampaignsFolder = folders.find(f => f.system_folder_id === 'my-campaigns');

        if (myCampaignsFolder) {
          // Small delay to ensure backend has fully processed campaign creation
          await new Promise(resolve => setTimeout(resolve, 100));
          await addCampaignToFolder(myCampaignsFolder.id, newCampaign.campaign_id);
        }
      } catch (folderError) {
        // Don't fail campaign creation if folder assignment fails
        // This is a non-critical operation - user can manually add to folder later
        logger.warn('[CampaignStore] Failed to add campaign to My Campaigns folder:', folderError);
        // Don't show error toast - campaign was created successfully
      }

      return newCampaign;
    } catch (error) {
      logger.error('[CampaignStore] Failed to create campaign:', error);
      const errorMessage = (error as Error).message || 'Failed to create campaign';
      toast.error(errorMessage);
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Duplicate existing campaign
   *
   * Creates a new campaign with same properties as the original campaign.
   * New campaign name will be: "{original_name} (copy)"
   * Does NOT copy organization selections.
   *
   * @param id - Campaign ID to duplicate
   * @example
   * ```tsx
   * await duplicateCampaign('CAMPAIGN_123');
   * ```
   */
  duplicateCampaign: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      // Find the original campaign
      const originalCampaign = get().campaigns.find((c) => c.campaign_id === id);
      if (!originalCampaign) {
        throw new Error(`Campaign ${id} not found`);
      }

      // Create new campaign with same properties (but NOT organization selections)
      const duplicateData: CampaignCreateRequest = {
        name: `${originalCampaign.campaign_name} (copy)`,
        route_config: originalCampaign.route_config ?? { start_location: '' },
        priority: toCampaignPriority(originalCampaign.priority),
        campaign_type: toCampaignType(originalCampaign.campaign_type),
        labels: originalCampaign.labels,
        tags: originalCampaign.tags,
        // Do NOT copy: label_contact_selections, status, starred, dates
      };

      const newCampaign = await campaignService.createCampaign(duplicateData);

      set((state) => ({
        campaigns: [...state.campaigns, newCampaign],
        isLoading: false,
      }));

      // Show success notification
      toast.success(`Campaign "${newCampaign.campaign_name}" created as duplicate!`);

      // Auto-add to "My Campaigns" system folder
      // Wait a bit to ensure campaign is fully persisted before folder assignment
      try {
        const { useFolderStore } = await import('../../navigation/store/folderStore');
        const { folders, addCampaignToFolder } = useFolderStore.getState();
        const myCampaignsFolder = folders.find(f => f.system_folder_id === 'my-campaigns');

        if (myCampaignsFolder) {
          // Small delay to ensure backend has fully processed campaign creation
          await new Promise(resolve => setTimeout(resolve, 100));
          await addCampaignToFolder(myCampaignsFolder.id, newCampaign.campaign_id);
        }
      } catch (folderError) {
        // Don't fail campaign duplication if folder assignment fails
        // This is a non-critical operation - user can manually add to folder later
        logger.warn('[CampaignStore] Failed to add duplicate campaign to My Campaigns folder:', folderError);
        // Don't show error toast - campaign was duplicated successfully
      }
    } catch (error) {
      logger.error('[CampaignStore] Failed to duplicate campaign:', error);
      const errorMessage = (error as Error).message || 'Failed to duplicate campaign';
      toast.error(errorMessage);
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Update existing campaign
   *
   * @deprecated Use `useUpdateCampaign()` from `@features/campaigns/queries` instead.
   * @param id - Campaign ID
   * @param data - Campaign update data (partial)
   * @example
   * ```tsx
   * // New way (TanStack Query with optimistic updates):
   * import { useUpdateCampaign } from '@features/campaigns/queries';
   * const { mutate } = useUpdateCampaign();
   * mutate({ id: 'CAMP_123', data: { name: 'Updated Name' } });
   * ```
   */
  updateCampaign: async (id: string, data: CampaignUpdateRequest) => {
    set({ isLoading: true, error: null });

    try {
      const updatedCampaign = await campaignService.updateCampaign(id, data);

      // ✅ FIX: Deep merge to preserve nested fields like route_config
      // This ensures all fields (including route_config, created_at, etc.) are properly updated
      // ✅ Force new array reference with spread to ensure React re-renders
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id
            ? {
                ...c,  // Keep existing fields
                ...updatedCampaign,  // Override with updated fields
                // Ensure nested objects are properly merged
                route_config: updatedCampaign.route_config || c.route_config,
              }
            : c
        )],
        isLoading: false,
      }));

      // Show success notification
      toast.success('Campaign updated successfully');
    } catch (error) {
      // Enhanced error logging with full details
      logger.error(`[CampaignStore] Failed to update campaign ${id}:`, error);

      // Extract meaningful error message
      const errorMessage =
        getErrorMessage(error) ||
        'Failed to update campaign. Please try again.';

      toast.error(errorMessage);

      set({
        error: errorMessage,
        isLoading: false,
      });

      throw error;
    }
  },

  /**
   * Delete campaign
   *
   * @deprecated Use `useDeleteCampaign()` from `@features/campaigns/queries` instead.
   * @param id - Campaign ID
   * @example
   * ```tsx
   * // New way (TanStack Query):
   * import { useDeleteCampaign } from '@features/campaigns/queries';
   * const { mutate } = useDeleteCampaign();
   * mutate('CAMP_123');
   * ```
   */
  deleteCampaign: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      // Get campaign name before deletion for notification
      const campaign = get().campaigns.find((c) => c.campaign_id === id);
      const campaignName = campaign?.campaign_name || 'Campaign';

      await campaignService.deleteCampaign(id);

      set((state) => ({
        campaigns: state.campaigns.filter((c) => c.campaign_id !== id),
        selectedIds: new Set(
          Array.from(state.selectedIds).filter((selectedId) => selectedId !== id)
        ),
        isLoading: false,
      }));

      // Show success notification
      toast.success(`"${campaignName}" deleted`);
    } catch (error) {
      logger.error(`[CampaignStore] Failed to delete campaign ${id}:`, error);
      const errorMessage = (error as Error).message || 'Failed to delete campaign';
      toast.error(errorMessage);
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Archive campaign
   *
   * @deprecated Use `useArchiveCampaign()` from `@features/campaigns/queries` instead.
   * @param id - Campaign ID
   * @example
   * ```tsx
   * // New way (TanStack Query):
   * import { useArchiveCampaign } from '@features/campaigns/queries';
   * const { mutate } = useArchiveCampaign();
   * mutate('CAMP_123');
   * ```
   */
  archiveCampaign: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      const updatedCampaign = await campaignService.archiveCampaign(id);

      // ✅ Force new array reference to ensure React re-renders
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? updatedCampaign : c
        )],
        isLoading: false,
      }));

      // Show success notification
      toast.success(`"${updatedCampaign.campaign_name}" archived`);
    } catch (error) {
      logger.error(`[CampaignStore] Failed to archive campaign ${id}:`, error);
      const errorMessage = (error as Error).message || 'Failed to archive campaign';
      toast.error(errorMessage);
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Unarchive campaign
   *
   * @param id - Campaign ID
   * @example
   * ```tsx
   * await campaignService.unarchiveCampaign('CAMPAIGN_123');
   * ```
   */
  unarchiveCampaign: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      const updatedCampaign = await campaignService.unarchiveCampaign(id);

      // ✅ Force new array reference to ensure React re-renders
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? updatedCampaign : c
        )],
        isLoading: false,
      }));
    } catch (error) {
      logger.error(`[CampaignStore] Failed to unarchive campaign ${id}:`, error);
      set({
        error: (error as Error).message || 'Failed to unarchive campaign',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Activate campaign
   *
   * @param id - Campaign ID
   * @example
   * ```tsx
   * await activateCampaign('CAMPAIGN_123');
   * ```
   */
  activateCampaign: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      const updatedCampaign = await campaignService.updateCampaign(id, { status: 'active' });

      // ✅ Force new array reference to ensure React re-renders
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? updatedCampaign : c
        )],
        isLoading: false,
      }));
    } catch (error) {
      logger.error(`[CampaignStore] Failed to activate campaign ${id}:`, error);
      set({
        error: (error as Error).message || 'Failed to activate campaign',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Deactivate campaign (set to archived)
   *
   * @param id - Campaign ID
   * @example
   * ```tsx
   * await deactivateCampaign('CAMPAIGN_123');
   * ```
   */
  deactivateCampaign: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      const updatedCampaign = await campaignService.updateCampaign(id, { status: 'archived' });

      // ✅ Force new array reference to ensure React re-renders
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? updatedCampaign : c
        )],
        isLoading: false,
      }));
    } catch (error) {
      logger.error(`[CampaignStore] Failed to deactivate campaign ${id}:`, error);
      set({
        error: (error as Error).message || 'Failed to deactivate campaign',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Set selected campaign (for filtering other views)
   * Persists to PostgreSQL for cross-device/session persistence.
   *
   * @param id - Campaign ID or null to clear selection
   * @example
   * ```tsx
   * setSelectedCampaign('CAMPAIGN_123');
   * setSelectedCampaign(null); // Clear selection
   * ```
   */
  setSelectedCampaign: (id: string | null) => {
    set({
      selectedCampaignId: id,
      lastCampaignLoadedAt: id ? new Date().toISOString() : null,
    });

    // Persist to PostgreSQL (non-blocking, fire-and-forget)
    if (id) {
      setLastSelectedCampaign(id).catch((error) => {
        logger.warn('[CampaignStore] Failed to persist campaign selection to cloud:', error);
      });
    }
  },

  /**
   * Rename campaign (convenience method)
   *
   * @param id - Campaign ID
   * @param newName - New campaign name
   * @example
   * ```tsx
   * await renameCampaign('CAMPAIGN_123', 'New Name');
   * ```
   */
  renameCampaign: async (id: string, newName: string) => {
    await get().updateCampaign(id, { name: newName });
  },

  /**
   * Update campaign priority (convenience method)
   *
   * @param id - Campaign ID
   * @param priority - New priority ('urgent' | 'high' | 'medium' | 'low')
   * @example
   * ```tsx
   * await updateCampaignPriority('CAMPAIGN_123', 'urgent');
   * ```
   */
  updateCampaignPriority: async (id: string, priority: CampaignPriority) => {
    // Store original value for rollback
    const originalCampaign = get().campaigns.find(c => c.campaign_id === id);

    if (!originalCampaign) {
      logger.error(`[CampaignStore] Campaign ${id} not found`);
      throw new Error(`Campaign ${id} not found`);
    }

    // Optimistic update - immediate UI feedback (force synchronous with flushSync)
    // ✅ Force new array reference to ensure React re-renders
    flushSync(() => {
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? { ...c, priority } : c
        )],
      }));
    });

    try {
      // Update backend
      await campaignService.updateCampaign(id, { priority });
    } catch (error) {
      logger.error(`[CampaignStore] ✗ Failed to update priority for ${id}:`, error);
      logger.error(`[CampaignStore] Error details:`, error);

      // Rollback to original value on error (force synchronous with flushSync)
      // ✅ Force new array reference to ensure React re-renders
      flushSync(() => {
        set((state) => ({
          campaigns: [...state.campaigns.map((c) =>
            c.campaign_id === id ? { ...c, priority: originalCampaign.priority } : c
          )],
        }));
      });

      throw error;
    }
  },

  /**
   * Update campaign status (convenience method)
   *
   * @param id - Campaign ID
   * @param status - New status ('planning' | 'future' | 'active' | 'completed' | 'archived')
   * @example
   * ```tsx
   * await updateCampaignStatus('CAMPAIGN_123', 'active');
   * ```
   */
  updateCampaignStatus: async (id: string, status: CampaignResponse['status']) => {
    // Store original value for rollback
    const originalCampaign = get().campaigns.find(c => c.campaign_id === id);

    if (!originalCampaign) {
      logger.error(`[CampaignStore] Campaign ${id} not found`);
      throw new Error(`Campaign ${id} not found`);
    }

    // Optimistic update - immediate UI feedback (force synchronous with flushSync)
    // ✅ Force new array reference to ensure React re-renders
    flushSync(() => {
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? { ...c, status } : c
        )],
      }));
    });

    try {
      // Update backend
      await campaignService.updateCampaign(id, { status });
    } catch (error) {
      logger.error(`[CampaignStore] ✗ Failed to update status for ${id}:`, error);
      logger.error(`[CampaignStore] Error details:`, error);

      // Rollback to original value on error (force synchronous with flushSync)
      // ✅ Force new array reference to ensure React re-renders
      flushSync(() => {
        set((state) => ({
          campaigns: [...state.campaigns.map((c) =>
            c.campaign_id === id ? { ...c, status: originalCampaign.status } : c
          )],
        }));
      });

      throw error;
    }
  },

  /**
   * Save organization selections to campaign
   *
   * IMPORTANT: This function handles both additions AND removals.
   * It compares current organizationIds with the original IDs loaded when campaign was opened
   * to determine which orgs to add and which to remove.
   *
   * @param campaignId - Campaign ID to save to
   * @param organizationIds - Array of current organization IDs (final selection)
   * @example
   * ```tsx
   * await saveToCampaign('CAMPAIGN_123', [3, 5, 7]);
   * ```
   */
  saveToCampaign: async (campaignId: string, organizationIds: number[]) => {
    // Store original campaign for rollback
    const originalCampaign = get().campaigns.find(c => c.campaign_id === campaignId);

    if (!originalCampaign) {
      logger.error(`[CampaignStore] Campaign ${campaignId} not found`);
      throw new Error(`Campaign ${campaignId} not found`);
    }

    // Get the original IDs from organization store (set when campaign was loaded)
    const originalSelectedIds = useOrganizationStore.getState().originalSelectedIds;
    const currentIds = new Set(organizationIds);
    const originalIds = new Set(originalSelectedIds);

    // Calculate which orgs to add and which to remove
    const toAdd = organizationIds.filter(id => !originalIds.has(id));
    const toRemove = originalSelectedIds.filter(id => !currentIds.has(id));

    logger.log(`[CampaignStore] Save analysis - Original: ${originalSelectedIds.length}, Current: ${organizationIds.length}`);
    logger.log(`[CampaignStore] To add: ${toAdd.length} orgs, To remove: ${toRemove.length} orgs`);

    // ✅ Optimistic update - update org_count immediately for UI feedback
    flushSync(() => {
      set((state) => ({
        campaigns: state.campaigns.map((c) =>
          c.campaign_id === campaignId
            ? {
                ...c,
                org_count: organizationIds.length
              }
            : c
        ),
      }));
    });

    try {
      // ✅ FIX: Handle removals first, then additions
      // This ensures we don't have duplicate orgs in transit

      // 1. Remove organizations that were deleted from selection
      if (toRemove.length > 0) {
        logger.log(`[CampaignStore] Removing ${toRemove.length} organizations from campaign...`);
        await campaignService.removeOrganizationsFromCampaign(campaignId, toRemove);
        logger.log(`[CampaignStore] ✓ Removed ${toRemove.length} organizations`);
      }

      // 2. Add new organizations
      let addResponse = { total_organizations: organizationIds.length };
      if (toAdd.length > 0) {
        logger.log(`[CampaignStore] Adding ${toAdd.length} organizations to campaign...`);
        addResponse = await campaignService.addOrganizationsToCampaign(campaignId, {
          organization_ids: toAdd
        });
        logger.log(`[CampaignStore] ✓ Added ${toAdd.length} organizations`);
      }

      // 3. Update label_contact_selections to reflect the final state
      const currentSelections = originalCampaign.label_contact_selections || {};
      const updatedSelections: Record<string, number[]> = {};

      // Only include orgs that are in the final selection
      for (const orgId of organizationIds) {
        const orgIdStr = String(orgId);
        // Preserve existing contact selections, or initialize with empty array
        updatedSelections[orgIdStr] = currentSelections[orgIdStr] || [];
      }

      // Update the contact selections
      const updatedCampaign = await campaignService.updateCampaignSelections(
        campaignId,
        updatedSelections
      );

      logger.log(`[CampaignStore] ✓ Updated contact selections for ${Object.keys(updatedSelections).length} organizations`);

      // Update the campaign in store with the full updated campaign data
      flushSync(() => {
        set((state) => ({
          campaigns: state.campaigns.map((c) =>
            c.campaign_id === campaignId
              ? {
                  ...c,
                  ...updatedCampaign,
                  org_count: organizationIds.length,
                  label_contact_selections: updatedSelections
                }
              : c
          ),
        }));
      });

      // Update both selectedFinalIds and originalSelectedIds to reflect the new saved state
      // (after save, the current selection becomes the new "original")
      const orgStore = useOrganizationStore.getState();
      orgStore.setSelectedFinalIds(organizationIds);
      orgStore.setOriginalSelectedIds(organizationIds);

    } catch (error) {
      logger.error('[CampaignStore] ✗ Failed to save to campaign:', error);
      logger.error(`[CampaignStore] Error details:`, error);

      // ✅ Rollback to original state on error (force synchronous with flushSync)
      flushSync(() => {
        set((state) => ({
          campaigns: state.campaigns.map((c) =>
            c.campaign_id === campaignId
              ? originalCampaign
              : c
          ),
        }));
      });

      const errorMessage = (error as Error).message || 'Failed to save to campaign';
      set({ error: errorMessage });
      throw error;
    }
  },

  /**
   * Toggle campaign selection
   *
   * @param id - Campaign ID
   */
  toggleSelection: (id: string) => {
    set((state) => {
      const newSet = new Set(state.selectedIds);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return { selectedIds: newSet };
    });
  },

  /**
   * Clear all selections
   */
  clearSelection: () => {
    set({ selectedIds: new Set() });
  },

  /**
   * Select all campaigns (filtered)
   */
  selectAll: () => {
    const filtered = get().getFilteredCampaigns();
    const allIds = new Set(filtered.map((c) => c.campaign_id));
    set({ selectedIds: allIds });
  },

  /**
   * Set search query
   *
   * @param query - Search string
   */
  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  /**
   * Set status filter
   *
   * @param filter - Status filter ('all' | 'active' | 'archived')
   */
  setStatusFilter: (filter: 'all' | 'active' | 'archived') => {
    set({ statusFilter: filter });
  },

  /**
   * Get filtered campaigns based on search and status filter
   *
   * @returns Filtered campaigns
   */
  getFilteredCampaigns: (): CampaignResponse[] => {
    const { campaigns, searchQuery, statusFilter, filters, sortField, sortDirection, activeCategory } = get();

    if (!campaigns || campaigns.length === 0) {
      return [];
    }

    // Filter campaigns
    let filtered = campaigns.filter((campaign) => {
      // Search filter (legacy + new filters)
      const query = searchQuery || filters.search || '';
      const matchesSearch =
        query === '' ||
        (campaign.campaign_name ?? '').toLowerCase().includes(query.toLowerCase());

      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && campaign.status === 'active') ||
        (statusFilter === 'archived' && campaign.status === 'archived');

      // Label filter
      const matchesLabel =
        !filters.labels ||
        filters.labels.length === 0 ||
        (campaign.labels || []).some((label) => filters.labels?.includes(label.id));

      // Tag filter
      const matchesTag =
        !filters.tags ||
        filters.tags.length === 0 ||
        (campaign.tags || []).some((tag) => filters.tags?.includes(tag));

      // Starred filter
      const matchesStarred =
        filters.starred === undefined ||
        campaign.starred === filters.starred;

      // Priority filter
      const matchesPriority =
        !filters.priority ||
        filters.priority.length === 0 ||
        filters.priority.includes(campaign.priority as CampaignPriority);

      // Campaign type filter
      const matchesType =
        !filters.campaign_type ||
        filters.campaign_type.length === 0 ||
        filters.campaign_type.includes(campaign.campaign_type as CampaignType);

      // Owner filter
      const matchesOwner =
        !filters.owner ||
        campaign.owner === filters.owner;

      // Due date filter
      const matchesDueDate = () => {
        if (!campaign.due_date) return true;
        const dueDate = new Date(campaign.due_date);
        const fromMatch =
          !filters.due_date_from ||
          dueDate >= new Date(filters.due_date_from);
        const toMatch =
          !filters.due_date_to ||
          dueDate <= new Date(filters.due_date_to);
        return fromMatch && toMatch;
      };

      return (
        matchesSearch &&
        matchesStatus &&
        matchesLabel &&
        matchesTag &&
        matchesStarred &&
        matchesPriority &&
        matchesType &&
        matchesOwner &&
        matchesDueDate()
      );
    });

    // Apply category filter (from sidebar navigation)
    if (activeCategory) {
      const [type, value] = activeCategory.split(':');
      if (type === 'priority') {
        filtered = filtered.filter(c => c.priority === value);
      } else if (type === 'status') {
        filtered = filtered.filter(c => c.status === value);
      } else if (type === 'standalone' && value === 'active') {
        filtered = filtered.filter(c => c.status === 'active');
      }
    }

    // Sort campaigns
    if (sortField) {
      filtered.sort((a, b) => {
        let aVal: string | number | null | undefined = a[sortField as keyof typeof a] as string | number | null | undefined;
        let bVal: string | number | null | undefined = b[sortField as keyof typeof b] as string | number | null | undefined;

        // Custom status order: active > ongoing > in_progress > future > planning > completed > archived
        if (sortField === 'status') {
          const statusOrder: Record<string, number> = {
            active: 0,
            ongoing: 1,
            in_progress: 2,
            future: 3,
            planning: 4,
            completed: 5,
            archived: 6,
          };
          aVal = statusOrder[a.status] ?? 999;
          bVal = statusOrder[b.status] ?? 999;
        }

        // Priority order: urgent > high > medium > low
        if (sortField === 'priority') {
          const priorityOrder: Record<string, number> = {
            urgent: 0,
            high: 1,
            medium: 2,
            low: 3,
          };
          aVal = priorityOrder[a.priority as string] ?? 999;
          bVal = priorityOrder[b.priority as string] ?? 999;
        }

        // Date fields
        if (sortField === 'last_modified') {
          aVal = a.last_modified ? new Date(a.last_modified).getTime() : 0;
          bVal = b.last_modified ? new Date(b.last_modified).getTime() : 0;
        }

        // Numeric fields
        if (sortField === 'org_count' || sortField === 'progress_percentage') {
          aVal = aVal ?? 0;
          bVal = bVal ?? 0;
        }

        // String fields (case-insensitive)
        if (sortField === 'campaign_name' || sortField === 'owner') {
          aVal = (aVal ?? '').toString().toLowerCase();
          bVal = (bVal ?? '').toString().toLowerCase();
        }

        // Compare values
        aVal = aVal ?? '';
        bVal = bVal ?? '';
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  },

  /**
   * Set advanced filters
   *
   * @param filters - Partial filter state
   */
  setFilters: (filters: Partial<CampaignFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  /**
   * Clear all filters
   */
  clearFilters: () => {
    set({
      filters: {
        status: 'all',
        labels: [],
        tags: [],
        starred: undefined,
        priority: [],
        campaign_type: [],
        owner: undefined,
        due_date_from: undefined,
        due_date_to: undefined,
        search: '',
      },
      searchQuery: '',
      statusFilter: 'all',
    });
  },

  /**
   * Set sorting field and direction
   *
   * @param field - Field to sort by
   * @param direction - Sort direction ('asc' | 'desc')
   */
  setSorting: (field: CampaignStore['sortField'], direction: 'asc' | 'desc') => {
    set({ sortField: field, sortDirection: direction });
  },

  /**
   * Toggle category expansion in sidebar
   *
   * @param category - Category name ('priority', 'status', etc.)
   */
  toggleCategory: (category: string) => {
    set((state) => {
      const newSet = new Set(state.expandedCategories);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return { expandedCategories: newSet };
    });
  },

  /**
   * Set active category for filtering
   *
   * @param category - Category string ('priority:high', 'status:active', 'standalone:active') or null to clear
   */
  setActiveCategory: (category: string | null) => {
    set({ activeCategory: category });
  },

  /**
   * Set view mode
   *
   * @param mode - View mode (list, kanban, calendar)
   */
  setViewMode: (mode: CampaignViewMode) => {
    set({ viewMode: mode });
  },

  /**
   * Toggle campaign star/favorite
   *
   * @deprecated Use `useToggleCampaignStar()` from `@features/campaigns/queries` instead.
   * @param id - Campaign ID
   */
  toggleCampaignStar: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      const campaign = get().campaigns.find((c) => c.campaign_id === id);
      if (!campaign) {
        throw new Error(`Campaign ${id} not found`);
      }

      const newStarred = !campaign.starred;
      const updated = await campaignService.toggleCampaignStar(id, newStarred);

      // ✅ Force new array reference to ensure React re-renders
      set((state) => {
        const newStarredSet = new Set(state.starredCampaigns);
        if (newStarred) {
          newStarredSet.add(id);
        } else {
          newStarredSet.delete(id);
        }

        return {
          campaigns: [...state.campaigns.map((c) =>
            c.campaign_id === id ? updated : c
          )],
          starredCampaigns: newStarredSet,
          isLoading: false,
        };
      });

      // Show success notification
      toast.success(newStarred ? 'Campaign starred' : 'Campaign unstarred');
    } catch (error) {
      logger.error(`[CampaignStore] Failed to toggle star ${id}:`, error);
      const errorMessage = (error as Error).message || 'Failed to toggle star';
      toast.error(errorMessage);
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Add labels to campaign
   *
   * @param id - Campaign ID
   * @param labels - Labels to add
   */
  addLabelsToCampaign: async (id: string, labels: CampaignLabel[]) => {
    set({ isLoading: true, error: null });

    try {
      const updated = await campaignService.addLabelsToCampaign(id, labels);

      // ✅ Force new array reference to ensure React re-renders
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? updated : c
        )],
        isLoading: false,
      }));

      // Update available labels
      get().fetchAvailableLabels();
    } catch (error) {
      logger.error(`[CampaignStore] Failed to add labels to campaign ${id}:`, error);
      set({
        error: (error as Error).message || 'Failed to add labels',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Remove labels from campaign
   *
   * @param id - Campaign ID
   * @param labelIds - Label IDs to remove
   */
  removeLabelsFromCampaign: async (id: string, labelIds: string[]) => {
    set({ isLoading: true, error: null });

    try {
      const updated = await campaignService.removeLabelsFromCampaign(id, labelIds);

      // ✅ Force new array reference to ensure React re-renders
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? updated : c
        )],
        isLoading: false,
      }));

      // Update available labels
      get().fetchAvailableLabels();
    } catch (error) {
      logger.error(`[CampaignStore] Failed to remove labels from campaign ${id}:`, error);
      set({
        error: (error as Error).message || 'Failed to remove labels',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Add tags to campaign
   *
   * @param id - Campaign ID
   * @param tags - Tags to add
   */
  addTagsToCampaign: async (id: string, tags: string[]) => {
    set({ isLoading: true, error: null });

    try {
      const updated = await campaignService.addTagsToCampaign(id, tags);

      // ✅ Force new array reference to ensure React re-renders
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? updated : c
        )],
        isLoading: false,
      }));
    } catch (error) {
      logger.error(`[CampaignStore] Failed to add tags to campaign ${id}:`, error);
      set({
        error: (error as Error).message || 'Failed to add tags',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Remove tags from campaign
   *
   * @param id - Campaign ID
   * @param tags - Tags to remove
   */
  removeTagsFromCampaign: async (id: string, tags: string[]) => {
    set({ isLoading: true, error: null });

    try {
      const updated = await campaignService.removeTagsFromCampaign(id, tags);

      // ✅ Force new array reference to ensure React re-renders
      set((state) => ({
        campaigns: [...state.campaigns.map((c) =>
          c.campaign_id === id ? updated : c
        )],
        isLoading: false,
      }));
    } catch (error) {
      logger.error(`[CampaignStore] Failed to remove tags from campaign ${id}:`, error);
      set({
        error: (error as Error).message || 'Failed to remove tags',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Bulk update multiple campaigns
   *
   * @param request - Bulk update request
   */
  bulkUpdateCampaigns: async (request: BulkUpdateRequest) => {
    set({ isLoading: true, error: null });

    try {
      const result = await campaignService.bulkUpdateCampaigns(request);

      logger.error(`[CampaignStore] Bulk updated ${result.updated_count} campaigns, ${result.failed_count} failed`);

      // Refresh campaigns after bulk update
      await get().fetchCampaigns();

      set({ isLoading: false });
    } catch (error) {
      logger.error('[CampaignStore] Failed to bulk update campaigns:', error);
      set({
        error: (error as Error).message || 'Failed to bulk update campaigns',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Bulk star/unstar campaigns
   *
   * @param campaignIds - Campaign IDs to update
   * @param starred - Star (true) or unstar (false)
   * @example
   * ```tsx
   * await bulkStarCampaigns(['CAMPAIGN_1', 'CAMPAIGN_2'], true);
   * ```
   */
  bulkStarCampaigns: async (campaignIds: string[], starred: boolean) => {
    set({ isLoading: true, error: null });

    try {
      const result = await campaignService.bulkStarCampaigns(campaignIds, starred);

      logger.error(`[CampaignStore] Bulk ${starred ? 'starred' : 'unstarred'} ${result.updated_count} campaigns, ${result.failed_count} failed`);

      // Refresh campaigns after bulk star operation
      await get().fetchCampaigns();

      set({ isLoading: false });
    } catch (error) {
      logger.error('[CampaignStore] Failed to bulk star campaigns:', error);
      set({
        error: (error as Error).message || 'Failed to bulk star campaigns',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Fetch available labels from all campaigns
   */
  fetchAvailableLabels: () => {
    const { campaigns } = get();

    // Extract unique labels from all campaigns
    const labelMap = new Map<string, CampaignLabel>();

    campaigns.forEach((campaign) => {
      if (campaign.labels && campaign.labels.length > 0) {
        campaign.labels.forEach((label) => {
          labelMap.set(label.id, label);
        });
      }
    });

    const availableLabels = Array.from(labelMap.values());

    set({ availableLabels });
  },

  /**
   * Create/add a new label to available labels
   *
   * @param label - Label to add
   */
  createLabel: (label: CampaignLabel) => {
    set((state) => {
      const existingIndex = state.availableLabels.findIndex((l) => l.id === label.id);
      if (existingIndex >= 0) {
        // Update existing label
        const updated = [...state.availableLabels];
        updated[existingIndex] = label;
        return { availableLabels: updated };
      } else {
        // Add new label
        return { availableLabels: [...state.availableLabels, label] };
      }
    });
  },

  /**
   * Update existing label in available labels
   *
   * @param label - Updated label
   */
  updateLabel: (label: CampaignLabel) => {
    set((state) => ({
      availableLabels: state.availableLabels.map((l) =>
        l.id === label.id ? label : l
      ),
    }));
  },

  /**
   * Delete label from available labels
   *
   * @param labelId - Label ID to delete
   */
  deleteLabel: (labelId: string) => {
    set((state) => ({
      availableLabels: state.availableLabels.filter((l) => l.id !== labelId),
    }));
  },

  /**
   * Fetch available tags from all campaigns
   */
  fetchAvailableTags: () => {
    const { campaigns } = get();

    // Extract unique tags from all campaigns
    const tagSet = new Set<string>();

    campaigns.forEach((campaign) => {
      if (campaign.tags && campaign.tags.length > 0) {
        campaign.tags.forEach((tag) => {
          tagSet.add(tag);
        });
      }
    });

    const availableTags = Array.from(tagSet).sort();

    set({ availableTags });
  },

  /**
   * Update campaign with retry logic
   *
   * Helper method that retries failed campaign updates with exponential backoff.
   * Used internally by updateCampaignPriority and other convenience methods.
   *
   * @param id - Campaign ID
   * @param data - Campaign update data
   * @param maxRetries - Maximum number of retry attempts (default: 3)
   * @example
   * ```tsx
   * await updateCampaignWithRetry('CAMPAIGN_123', { priority: 'high' }, 3);
   * ```
   */
  updateCampaignWithRetry: async (
    id: string,
    data: CampaignUpdateRequest,
    maxRetries: number = 3
  ) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await get().updateCampaign(id, data);
      } catch (error) {
        logger.warn(`[CampaignStore] Update attempt ${attempt}/${maxRetries} failed:`, error);

        if (attempt === maxRetries) {
          throw error; // Final attempt failed
        }

        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt - 1) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },
}),
    {
      name: 'campaign-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        campaigns: state.campaigns,
        selectedCampaignId: state.selectedCampaignId,
        lastCampaignLoadedAt: state.lastCampaignLoadedAt,
        availableLabels: state.availableLabels,
        availableTags: state.availableTags,
        filters: state.filters,
        viewMode: state.viewMode,
        starredCampaigns: state.starredCampaigns,
        sortField: state.sortField,
        sortDirection: state.sortDirection,
        expandedCategories: Array.from(state.expandedCategories), // Convert Set to Array for JSON
        // Exclude: isLoading, error, selectedIds, searchQuery, statusFilter, activeCategory
      }),
      // Convert expandedCategories array back to Set on rehydration
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<CampaignStore>),
        // Ensure expandedCategories is always a Set
        expandedCategories: new Set(
          (persistedState as { expandedCategories?: string[] })?.expandedCategories ??
          Array.from(currentState.expandedCategories)
        ),
      }),
    }
  )
);
